import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styles from "./ClientAnalysis.module.scss";
import { Card, Table, Select, Typography, Button, Alert, Skeleton } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
} from "chart.js";
import { getAllChannels, getAllBranchOffices, getClientAnalysis } from "../../../api/Services";
import { CLIENT_STATE_KEYS, ClientStateKey, TransitionType } from "./clientAnalysisMock";
import type { ClientAnalysisResponse } from "../../../types/clientAnalysis";

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip);

const { Title, Text } = Typography;

const fmt = (v: number) =>
  `$ ${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");
const fmtM = (v: number) => `$ ${v.toFixed(1)}M`;

const CHART_COLORS: Record<string, string> = {
  new: "#722ed1",
  active: "#531dab",
  recurrent: "#eb2f96",
  vip: "#faad14",
  atRisk: "#ffc53d",
  lost: "#ff7875",
};

const Vr: React.FC<{ value: number | null | undefined; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (value === null || value === undefined) {
    return <span className={styles.variation}>—</span>;
  }
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const heatColor = (v: number | null): string => {
  if (v === null) return "#f5f5f5";
  if (v >= 60) return "#722ed1";
  if (v >= 45) return "#9254de";
  if (v >= 35) return "#b37feb";
  if (v >= 25) return "#d3adf7";
  return "#efdbff";
};

const ClientAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30");
  const [channelId, setChannelId] = useState<number | undefined>();
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>();
  const [data, setData] = useState<ClientAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [channelOptions, setChannelOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [storeOptions, setStoreOptions] = useState<
    { value: number; label: string }[]
  >([]);

  useEffect(() => {
    void Promise.all([getAllChannels(), getAllBranchOffices()]).then(
      ([channels, stores]) => {
        setChannelOptions(
          (channels ?? []).map((c: { id: number; type?: { type?: string } }) => ({
            value: c.id,
            label: c.type?.type ?? String(c.id),
          }))
        );
        setStoreOptions(
          (stores ?? []).map((b: { id: number; name: string }) => ({
            value: b.id,
            label: b.name,
          }))
        );
      }
    );
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    getClientAnalysis(Number(period), channelId, branchOfficeId)
      .then((res) => {
        if (!res) {
          setLoadError(true);
          setData(null);
        } else {
          setData(res);
        }
      })
      .catch(() => {
        setLoadError(true);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [period, channelId, branchOfficeId]);

  const stateLabel = (key: ClientStateKey | string) =>
    t(`ClientAnalysis-State-${key}`);

  const transitionTypeLabel = (type: TransitionType) =>
    t(`ClientAnalysis-TransitionType-${type}`);

  const chartData = useMemo(() => {
    if (!data?.dynamicChart) {
      return { labels: [] as string[], datasets: [] };
    }
    const dc = data.dynamicChart;
    const series: { key: string; values: number[] }[] = [
      { key: "new", values: dc.newClients ?? [] },
      { key: "active", values: dc.active ?? [] },
      { key: "recurrent", values: dc.recurrent ?? [] },
      { key: "vip", values: dc.vip ?? [] },
      { key: "atRisk", values: dc.atRisk ?? [] },
      { key: "lost", values: dc.lost ?? [] },
    ];
    return {
      labels: data.chartMonths ?? [],
      datasets: series.map(({ key, values }) => ({
        label: stateLabel(key),
        data: values,
        backgroundColor: CHART_COLORS[key],
        borderRadius: 4,
        stack: "cycle",
      })),
    };
  }, [data, t]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index" as const, intersect: false },
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true },
    },
  };

  const navLinks = [
    {
      title: t("ClientAnalysis-Nav-Opportunities"),
      desc: t("ClientAnalysis-Nav-Opportunities-Desc"),
      path: "/commercial-analysis/summary",
    },
    {
      title: t("ClientAnalysis-Nav-Stores"),
      desc: t("ClientAnalysis-Nav-Stores-Desc"),
      path: "/commercial-analysis/stores",
    },
    {
      title: t("ClientAnalysis-Nav-Channels"),
      desc: t("ClientAnalysis-Nav-Channels-Desc"),
      path: "/commercial-analysis/channels",
    },
    {
      title: t("ClientAnalysis-Nav-Activation"),
      desc: t("ClientAnalysis-Nav-Activation-Desc"),
      path: "/commercial-intelligence/opportunities/activation-actions",
    },
  ];

  if (loading && !data) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.pageHeader}>
          <Skeleton.Input active size="small" style={{ width: 120, height: 20, marginBottom: 12 }} />
          <Skeleton.Input active style={{ width: 320, height: 32, marginBottom: 8 }} />
          <Skeleton.Input active size="small" style={{ width: 420, height: 16 }} />
        </div>
        <div className={styles.kpiGrid}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          ))}
        </div>
        <Card style={{ marginTop: 16 }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {loadError && (
        <Alert type="error" message={t("ClientAnalysis-LoadError")} showIcon style={{ marginBottom: 16 }} />
      )}
      <Title level={3} className={styles.pageTitle}>
        {t("ClientAnalysis-PageTitle")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("ClientAnalysis-Period")}</Text>
            <Select
              className={styles.filterSelect}
              value={period}
              onChange={setPeriod}
              options={[
                { value: "30", label: t("ClientAnalysis-Period-30") },
                { value: "60", label: t("ClientAnalysis-Period-60") },
                { value: "90", label: t("ClientAnalysis-Period-90") },
                { value: "180", label: t("ClientAnalysis-Period-180") },
              ]}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("ClientAnalysis-Filter-Channel")}</Text>
            <Select
              className={styles.filterSelect}
              placeholder={t("ClientAnalysis-Filter-AllChannels")}
              allowClear
              value={channelId}
              onChange={setChannelId}
              options={channelOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("ClientAnalysis-Filter-Store")}</Text>
            <Select
              className={styles.filterSelect}
              placeholder={t("ClientAnalysis-Filter-AllStores")}
              allowClear
              value={branchOfficeId}
              onChange={setBranchOfficeId}
              options={storeOptions}
            />
          </div>
        </div>
      </Card>

      <div className={styles.kpiGrid}>
        {(data?.kpis ?? []).map((kpi) => (
          <Card className={styles.kpiCard} key={kpi.key} bordered={false}>
            <Text className={styles.kpiLabel}>
              {t(`ClientAnalysis-Kpi-${kpi.key}`)}
            </Text>
            <div className={styles.kpiValue}>
              {kpi.isMoney
                ? fmt(kpi.value)
                : kpi.isPct
                  ? `${kpi.value.toFixed(1)}%`
                  : kpi.dec
                    ? kpi.value.toFixed(1)
                    : fmtN(kpi.value)}
              {kpi.unitDays ? ` ${t("ClientAnalysis-Days")}` : ""}
            </div>
            <Vr value={kpi.variation ?? null} />
            <Text className={styles.kpiHint}>
              {t(`ClientAnalysis-Kpi-${kpi.key}-Hint`)}
            </Text>
          </Card>
        ))}
      </div>

      <div className={styles.twoColRow}>
        <Card
          className={styles.sectionCard}
          bordered={false}
          title={t("ClientAnalysis-Section-Composition")}
        >
          <div className={styles.compBar}>
            {(data?.states ?? []).map((s) => (
              <div
                key={s.key}
                className={styles.compSegment}
                style={{ width: `${s.pct}%`, backgroundColor: s.color }}
              />
            ))}
          </div>
          <div className={styles.compGrid}>
            {(data?.states ?? []).map((s) => (
              <div className={styles.compStateCard} key={s.key}>
                <div className={styles.compStateHead}>
                  <span className={styles.compStateName}>
                    <span className={styles.compDot} style={{ backgroundColor: s.color }} />
                    {stateLabel(s.key)}
                  </span>
                  <span className={styles.compPctBadge}>{s.pct.toFixed(1)}%</span>
                </div>
                <div className={styles.compCount}>{fmtN(s.count)}</div>
                <Text className={styles.compHint}>
                  {t("ClientAnalysis-StateClientsHint")}
                </Text>
              </div>
            ))}
          </div>
        </Card>

        <Card
          className={styles.sectionCard}
          bordered={false}
          title={t("ClientAnalysis-Section-Activation")}
        >
          <span
            className={styles.activationLink}
            onClick={() =>
              navigate("/commercial-intelligence/opportunities/activation-actions")
            }
          >
            {t("ClientAnalysis-Activation-Link")}
          </span>
          <div className={styles.activationMetrics}>
            <div className={styles.activationBlock}>
              <span className={styles.activationBlockLabel}>
                {t("ClientAnalysis-Activation-Reactivated")}
              </span>
              <div className={`${styles.activationBlockValue} ${styles.activationValueGreen}`}>
                {fmtN(data?.activation?.reactivatedIncentive ?? 0)}
              </div>
              <Text className={styles.activationBlockHint}>
                {t("ClientAnalysis-Activation-Reactivated-Hint")}
              </Text>
            </div>
            <div className={styles.activationBlock}>
              <span className={styles.activationBlockLabel}>
                {t("ClientAnalysis-Activation-NoReaction")}
              </span>
              <div className={`${styles.activationBlockValue} ${styles.activationValueRed}`}>
                {fmtN(data?.activation?.noReaction ?? 0)}
              </div>
              <Text className={styles.activationBlockHint}>
                {t("ClientAnalysis-Activation-NoReaction-Hint")}
              </Text>
            </div>
            <div className={styles.convGrid}>
              <div className={styles.convItem}>
                <span className={styles.convLabel}>
                  {t("ClientAnalysis-Activation-ReactionRate")}
                </span>
                <div className={styles.convValue}>
                  {(data?.activation?.reactionRate ?? 0).toFixed(1)}%
                </div>
                <Text className={styles.convHint}>
                  {t("ClientAnalysis-Activation-ReactionRate-Hint")}
                </Text>
              </div>
              <div className={styles.convItem}>
                <span className={styles.convLabel}>
                  {t("ClientAnalysis-Activation-InactiveReactivation")}
                </span>
                <div className={styles.convValue}>
                  {(data?.activation?.inactiveReactivationRate ?? 0).toFixed(1)}%
                </div>
                <Text className={styles.convHint}>
                  {t("ClientAnalysis-Activation-InactiveReactivation-Hint")}
                </Text>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className={styles.twoColRow}>
        <Card
          className={styles.sectionCard}
          bordered={false}
          title={t("ClientAnalysis-Section-Flow")}
          extra={
            <span className={styles.sectionHeadExtra}>
              {t("ClientAnalysis-Flow-Badge")}
            </span>
          }
        >
          <Table
            className={styles.dataTable}
            pagination={false}
            size="small"
            dataSource={data?.transitions ?? []}
            columns={[
              {
                title: t("ClientAnalysis-Column-Origin"),
                dataIndex: "origin",
                render: (v: string) => stateLabel(v),
              },
              {
                title: t("ClientAnalysis-Column-Destination"),
                dataIndex: "destination",
                render: (v: string) => stateLabel(v),
              },
              {
                title: t("ClientAnalysis-Column-Clients"),
                dataIndex: "count",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-Type"),
                dataIndex: "type",
                render: (v: TransitionType) => {
                  const typeClass =
                    v === "advance"
                      ? styles.typeAdvance
                      : v === "recovery"
                        ? styles.typeRecovery
                        : styles.typeDecline;
                  return (
                    <span className={`${styles.typeBadge} ${typeClass}`}>
                      {transitionTypeLabel(v)}
                    </span>
                  );
                },
              },
            ]}
          />
        </Card>

        <Card
          className={styles.sectionCard}
          bordered={false}
          title={t("ClientAnalysis-Section-Insights")}
        >
          <div className={styles.insightList}>
            {(data?.insights ?? []).map((ins) => {
              const severityClass =
                ins.severity === "positive"
                  ? styles.insightPositive
                  : ins.severity === "warning"
                    ? styles.insightWarning
                    : ins.severity === "info"
                      ? styles.insightInfo
                      : styles.insightAlert;
              return (
              <div
                key={ins.key}
                className={`${styles.insightBox} ${severityClass}`}
              >
                <Text className={styles.insightTitle}>
                  {t(`ClientAnalysis-Insight-${ins.key}-Title`)}
                </Text>
                <Text className={styles.insightDesc}>
                  {t(`ClientAnalysis-Insight-${ins.key}-Desc`)}
                </Text>
              </div>
            );
            })}
          </div>
        </Card>
      </div>

      <Card
        className={styles.sectionCard}
        bordered={false}
        title={t("ClientAnalysis-Section-EconomicValue")}
      >
          <Table
            className={styles.dataTable}
            pagination={false}
            size="small"
            dataSource={data?.economicValue ?? []}
            columns={[
              {
                title: t("ClientAnalysis-Column-State"),
                dataIndex: "state",
                render: (v: string) => stateLabel(v),
              },
              {
                title: t("ClientAnalysis-Column-Clients"),
                dataIndex: "clients",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-AvgTicket"),
                dataIndex: "ticket",
                align: "right" as const,
                render: (v: number) => fmt(v),
              },
              {
                title: t("ClientAnalysis-Column-Frequency"),
                dataIndex: "frequency",
                align: "right" as const,
                render: (v: number) => v.toFixed(1),
              },
              {
                title: t("ClientAnalysis-Column-TotalSales"),
                dataIndex: "salesM",
                align: "right" as const,
                render: (v: number) => fmtM(v),
              },
              {
                title: t("ClientAnalysis-Column-SalesPct"),
                dataIndex: "salesPct",
                align: "right" as const,
                render: (v: number) => `${v.toFixed(1)}%`,
              },
            ]}
          />
      </Card>

      <div className={styles.twoColRow}>
        <Card
          className={styles.sectionCard}
          bordered={false}
          title={t("ClientAnalysis-Section-Dynamics")}
        >
          <div className={styles.chartLegend}>
            {CLIENT_STATE_KEYS.map((key) => (
              <span className={styles.legendItem} key={key}>
                <span
                  className={styles.legendDot}
                  style={{ backgroundColor: CHART_COLORS[key] }}
                />
                {stateLabel(key)}
              </span>
            ))}
          </div>
          <div className={styles.chartWrap}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </Card>

        <Card
          className={styles.sectionCard}
          bordered={false}
          title={t("ClientAnalysis-Section-Navigation")}
        >
          <div className={styles.navList}>
            {navLinks.map((link) => (
              <div
                key={link.path}
                className={styles.navItem}
                onClick={() => navigate(link.path)}
              >
                <div>
                  <Text className={styles.navTitle}>{link.title}</Text>
                  <Text className={styles.navDesc}>{link.desc}</Text>
                </div>
                <RightOutlined style={{ color: "#8c8c8c" }} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card
        className={`${styles.sectionCard} ${styles.generationsSection}`}
        bordered={false}
        title={t("ClientAnalysis-Section-Generations")}
        extra={
          <span className={styles.sectionHeadExtra}>
            {t("ClientAnalysis-Generations-Badge")}
          </span>
        }
      >
        <div className={styles.generationsSplit}>
          <Table
            className={styles.dataTable}
            pagination={false}
            size="small"
            dataSource={data?.generations ?? []}
            columns={[
              { title: t("ClientAnalysis-Column-Generation"), dataIndex: "gen" },
              {
                title: t("ClientAnalysis-Column-Initial"),
                dataIndex: "initial",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-New"),
                dataIndex: "newClients",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-Active"),
                dataIndex: "active",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-Recurrent"),
                dataIndex: "recurrent",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-Vip"),
                dataIndex: "vip",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-AtRisk"),
                dataIndex: "atRisk",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-Lost"),
                dataIndex: "lost",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
              {
                title: t("ClientAnalysis-Column-AccumSales"),
                dataIndex: "salesM",
                align: "right" as const,
                render: (v: number) => fmtM(v),
              },
            ]}
          />

          <div>
            <Text className={styles.matrixSubtitle}>
              {t("ClientAnalysis-Matrix-Subtitle")}
            </Text>
            <span className={styles.rankingBadge} style={{ marginBottom: 12, display: "inline-block" }}>
              {t("ClientAnalysis-Matrix-Badge")}
            </span>
            <table className={styles.heatmapTable}>
              <thead>
                <tr>
                  <th className={styles.heatmapHeader}>
                    {t("ClientAnalysis-Column-Generation")}
                  </th>
                  {[0, 1, 2, 3, 4, 5].map((m) => (
                    <th key={m} className={styles.heatmapHeader}>
                      {`${t("ClientAnalysis-Month")} ${m}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.retentionMatrix ?? []).map((row) => (
                  <tr key={row.gen}>
                    <td className={styles.heatmapRowLabel}>{row.gen}</td>
                    {[row.m0, row.m1, row.m2, row.m3, row.m4, row.m5].map((v, i) => (
                      <td key={i} className={styles.heatmapCell}>
                        {v !== null ? (
                          <span
                            className={styles.heatmapPill}
                            style={{
                              backgroundColor: heatColor(v),
                              color: v >= 45 ? "#fff" : "#2d313e",
                            }}
                          >
                            {v}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <div className={styles.maturationRow}>
        {(
          [
            { key: "secondPurchase", value: data?.maturation?.secondPurchase },
            { key: "thirdPurchase", value: data?.maturation?.thirdPurchase },
            { key: "highValue", value: data?.maturation?.highValue },
          ] as const
        ).map((item) => (
          <div className={styles.maturationCard} key={item.key}>
            <span className={styles.maturationLabel}>
              {t(`ClientAnalysis-Maturation-${item.key}`)}
            </span>
            <div className={styles.maturationValue}>
              {item.value != null
                ? `${item.value} ${t("ClientAnalysis-Days")}`
                : "—"}
            </div>
          </div>
        ))}
      </div>

      <Card
        className={`${styles.sectionCard} ${styles.closingPanel}`}
        bordered={false}
        title={t("ClientAnalysis-Section-GenPerformance")}
        extra={<span className={styles.rankingBadge}>{t("ClientAnalysis-Ranking")}</span>}
      >
        <Table
          className={styles.dataTable}
          pagination={false}
          size="small"
          dataSource={data?.genPerformance ?? []}
          columns={[
            { title: t("ClientAnalysis-Column-Generation"), dataIndex: "gen" },
            {
              title: t("ClientAnalysis-Column-PctSecondPurchase"),
              dataIndex: "pctSecondPurchase",
              align: "right" as const,
              render: (v: number) => `${v}%`,
            },
            {
              title: t("ClientAnalysis-Column-PctThirdPurchase"),
              dataIndex: "pctThirdPurchase",
              align: "right" as const,
              render: (v: number) => `${v}%`,
            },
            {
              title: t("ClientAnalysis-Column-AvgSaleClient"),
              dataIndex: "avgSale",
              align: "right" as const,
              render: (v: number) => fmt(v),
            },
            {
              title: t("ClientAnalysis-Column-AvgDaysSecond"),
              dataIndex: "avgDaysToSecond",
              align: "right" as const,
              render: (v: number | null) =>
                v != null ? `${v} ${t("ClientAnalysis-Days")}` : "—",
            },
            {
              title: t("ClientAnalysis-Column-PctHighValue"),
              dataIndex: "pctHighValue",
              align: "right" as const,
              render: (v: number) => `${v}%`,
            },
          ]}
        />
        <div className={styles.bottomSplit}>
          {(data?.genInsights ?? []).map((ins, i) => (
            <div
              key={ins.key}
              className={`${styles.genInsightCard} ${i === 0 ? styles.genInsightPurple : styles.genInsightYellow}`}
            >
              <Text className={styles.insightTitle}>
                {t("ClientAnalysis-GenInsight-Title")}
              </Text>
              <Text className={styles.insightDesc}>
                {t(`ClientAnalysis-GenInsight-${ins.key}-Desc`)}
              </Text>
            </div>
          ))}
        </div>
      </Card>

      <div className={styles.exportRow}>
        <Button type="primary">{t("ClientAnalysis-Export")}</Button>
      </div>
    </div>
  );
};

export default ClientAnalysis;
